// description/ai/store/state.ts

import { writable } from 'svelte/store';
import type { RawAnnotation } from '../../../media/ai/store/vision-store';

/**
 * A reviewed annotation includes the original raw annotation data,
 * plus a status ('approved' or 'rejected') and a timestamp.
 */
export interface ReviewedAnnotation extends RawAnnotation {
    status: 'approved' | 'rejected';
    reviewedAt: Date;
}

/**
 * A Svelte store to hold all 'reviewed' annotations.
 */
export const reviewedAnnotations = writable<ReviewedAnnotation[]>([]);

/**
 * Additional Svelte stores for user input and generated steps,
 * retaining existing functionality.
 */
export const userInput = writable<string>('');
export interface Step {
    id: string;
    content: string;
    status: 'pending' | 'approved' | 'rejected';
}

export const generatedSteps = writable<Step[]>([]);


/**
 * Actions to approve or reject annotations, updating the reviewedAnnotations store.
 */
export const annotationActions = {
    approve: (annotation: RawAnnotation) => {
        reviewedAnnotations.update(annotations => {
            // Check if this annotation ID has already been reviewed
            if (annotations.some(a => a.id === annotation.id)) {
                return annotations;
            }

            const reviewed: ReviewedAnnotation = {
                ...annotation,
                status: 'approved',
                reviewedAt: new Date()
            };

            return [...annotations, reviewed];
        });
    },

    reject: (annotation: RawAnnotation) => {
        reviewedAnnotations.update(annotations => {
            // Check if this annotation ID has already been reviewed
            if (annotations.some(a => a.id === annotation.id)) {
                return annotations;
            }

            const reviewed: ReviewedAnnotation = {
                ...annotation,
                status: 'rejected',
                reviewedAt: new Date()
            };

            return [...annotations, reviewed];
        });
    }
};
